import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  Dialog,
  DialogContent,
  Grid,
  Alert,
  Divider,
  Chip,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { apiFetch } from "./utils/apiClient";
const API_BASE =
  process.env.NODE_ENV === "development"
    ? "http://localhost:5000/api"
    : "https://artikaorder-backend.onrender.com/api";

const defaultSettings = {
  title: "Artika AI Assistant",
  welcomeMessage: "ارفع صورة جدار الزبون",
  enabled: true,
  maxWallImages: 1,
  maxOptionImages: 6,

  option1Label: "ارفع صورة اللوحة التي يريدها الزبون",
  option2Label: "ارفع صورة اللوحة التي يرغبها الزبون لكن غير ألوان",
  option3Label: "ارفع الـ inspiration images",
  option4Label: "اكتب فكرة اللوحة التي يريدها الزبون",
};

const optionDescriptions = {
  option1:
    "ارفع صورة واحدة للوحة التي يريدها الزبون بالضبط، وسيتم تركيب نفس الصورة على الجدار بدون تغيير التصميم أو الألوان.",
  option2:
    "ارفع صورة اللوحة التي يريدها الزبون، واكتب الألوان المطلوبة أو الملاحظة.",
  option3:
    "ارفع صور الإلهام Inspiration Images حتى يتم بناء فكرة مناسبة منها.",
  option4:
    "اكتب فكرة اللوحة التي يريدها الزبون، ويمكنك رفع صور مساعدة إذا موجودة.",
};

const ArtikaAI = () => {
  const [settings, setSettings] = useState(defaultSettings);
  const [settingsLoading, setSettingsLoading] = useState(true);

  const [step, setStep] = useState("wall");
  // wall | wallResult | options | optionForm | finalResult

  const [wallFiles, setWallFiles] = useState([]);
  const [wallNote, setWallNote] = useState("");
  const [wallResult, setWallResult] = useState(null);
  const [selectedWallProvider, setSelectedWallProvider] = useState("");

  const [selectedOption, setSelectedOption] = useState("");
  const [optionFiles, setOptionFiles] = useState([]);
  const [optionNote, setOptionNote] = useState("");
  const [ideaText, setIdeaText] = useState("");
  const [finalResult, setFinalResult] = useState(null);
  const [selectedFinalProvider, setSelectedFinalProvider] = useState("");

  const [previewImage, setPreviewImage] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadSettings = async () => {
    try {
      setSettingsLoading(true);

      const res = await apiFetch(`${API_BASE}/ai/prompt`);
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Load AI settings failed");
      }

      setSettings({
        ...defaultSettings,
        ...data,
      });
    } catch (error) {
      console.error("Load AI settings error:", error);
      setSettings(defaultSettings);
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const createPreviewFiles = (files, limit) => {
    const imagesOnly = files.filter((file) => file.type.startsWith("image/"));

    if (imagesOnly.length !== files.length) {
      alert("حالياً هذه الواجهة تقبل الصور فقط.");
    }

    return imagesOnly.slice(0, limit).map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
  };

  const handleWallFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const preparedFiles = createPreviewFiles(files, 1);

    wallFiles.forEach((item) => {
      if (item.preview) URL.revokeObjectURL(item.preview);
    });

    setWallFiles(preparedFiles);
    e.target.value = "";
  };

  const handleOptionFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    const limit = Number(settings.maxOptionImages || 6);
    const preparedFiles = createPreviewFiles(files, limit);

    optionFiles.forEach((item) => {
      if (item.preview) URL.revokeObjectURL(item.preview);
    });

    setOptionFiles(preparedFiles);
    e.target.value = "";
  };

  const removeWallFile = (index) => {
    setWallFiles((prev) => {
      const item = prev[index];
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const removeOptionFile = (index) => {
    setOptionFiles((prev) => {
      const item = prev[index];
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const submitWallAnalysis = async () => {
    if (wallFiles.length !== 1) {
      alert("لازم ترفعين صورة جدار واحدة فقط.");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("note", wallNote || "");
      formData.append("files", wallFiles[0].file);

      const res = await apiFetch(`${API_BASE}/ai/wall-analysis`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
console.log("WALL ANALYSIS RESPONSE:", data);
      if (!res.ok) {
        throw new Error(
          data.details || data.error || "حدث خطأ أثناء تحليل صورة الجدار"
        );
      }

      setWallResult({
        openai: data.openai || null,
        gemini: data.gemini || null,
      });

      setSelectedWallProvider("");
      setStep("wallResult");
    } catch (error) {
      console.error("Wall analysis frontend error:", error);
      alert(`❌ ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const selectWallResult = (provider) => {
    setSelectedWallProvider(provider);
  };

const continueToOptions = () => {
  if (!selectedWallProvider) {
    alert("اختاري نتيجة تحليل الجدار حتى نكمل.");
    return;
  }

  // ✅ Test Mode:
  // نوقف الخيارات الأربعة مؤقتًا ونروح مباشرة لرفع صورة اللوحة
  setSelectedOption("option1");
  setOptionFiles([]);
  setOptionNote("");
  setIdeaText("");
  setFinalResult(null);
  setStep("optionForm");
};

  const chooseOption = (optionType) => {
    setSelectedOption(optionType);
    setOptionFiles([]);
    setOptionNote("");
    setIdeaText("");
    setFinalResult(null);
    setSelectedFinalProvider("");
    setStep("optionForm");
  };

  const getSelectedWallText = () => {
    if (!wallResult || !selectedWallProvider) return "";

    return wallResult[selectedWallProvider]?.textReply || "";
  };

  const submitOptionAnalysis = async () => {
    if (!selectedOption) {
      alert("اختاري واحد من الخيارات.");
      return;
    }

    if (
      selectedOption !== "option4" &&
      optionFiles.length === 0 &&
      !optionNote.trim()
    ) {
      alert("ارفعي صورة أو اكتبي ملاحظة حتى نكمل التحليل.");
      return;
    }

    if (
      selectedOption === "option4" &&
      !ideaText.trim() &&
      optionFiles.length === 0
    ) {
      alert("اكتبي فكرة اللوحة أو ارفعي صورة مساعدة.");
      return;
    }

    try {
      setLoading(true);

const formData = new FormData();

formData.append("optionType", selectedOption);
formData.append("note", optionNote || "");
formData.append("ideaText", ideaText || "");

// ✅ معلومات الخطوة الأولى
formData.append("wallNote", wallNote || "");
formData.append("wallResultText", getSelectedWallText());
formData.append("selectedWallProvider", selectedWallProvider || "openai");

// ✅ نرسل صورة الجدار الأصلية للباك باسم wallImage
if (wallFiles[0]?.file) {
  formData.append("wallImage", wallFiles[0].file);
}

// ✅ صور اللوحة الحالية
optionFiles.forEach((item) => {
  formData.append("files", item.file);
});

      const res = await apiFetch(`${API_BASE}/ai/option-analysis`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data.details || data.error || "حدث خطأ أثناء تنفيذ الخيار"
        );
      }

      setFinalResult({
        openai: data.openai || null,
        gemini: data.gemini || null,
      });

      setSelectedFinalProvider("");
      setStep("finalResult");
    } catch (error) {
      console.error("Option analysis frontend error:", error);
      alert(`❌ ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const selectFinalResult = (provider) => {
    setSelectedFinalProvider(provider);
  };

  const resetWorkflow = () => {
    wallFiles.forEach((item) => {
      if (item.preview) URL.revokeObjectURL(item.preview);
    });

    optionFiles.forEach((item) => {
      if (item.preview) URL.revokeObjectURL(item.preview);
    });

    setStep("wall");
    setWallFiles([]);
    setWallNote("");
    setWallResult(null);
    setSelectedWallProvider("");
    setSelectedOption("");
    setOptionFiles([]);
    setOptionNote("");
    setIdeaText("");
    setFinalResult(null);
    setSelectedFinalProvider("");
    setPreviewImage(null);
  };

  const getOptionLabel = (optionType) => {
    const map = {
      option1: settings.option1Label,
      option2: settings.option2Label,
      option3: settings.option3Label,
      option4: settings.option4Label,
    };

    return map[optionType] || optionType;
  };

  const renderSelectedImagePreview = (files, removeFn) => {
    if (!files.length) return null;

    return (
      <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
        {files.map((item, index) => (
          <Box
            key={index}
            sx={{
              position: "relative",
              width: 90,
              height: 90,
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid #ddd",
              backgroundColor: "#fff",
            }}
          >
            <img
              src={item.preview}
              alt={item.file.name}
              onClick={() => setPreviewImage(item.preview)}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                cursor: "pointer",
                display: "block",
              }}
            />

            <IconButton
              size="small"
              onClick={() => removeFn(index)}
              sx={{
                position: "absolute",
                top: 2,
                right: 2,
                backgroundColor: "rgba(0,0,0,0.55)",
                color: "#fff",
                width: 24,
                height: 24,
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.75)",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        ))}
      </Box>
    );
  };

  const renderGeneratedImages = (images = []) => {
    if (!images.length) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography fontWeight="bold" sx={{ mb: 1 }}>
          الصور الناتجة:
        </Typography>

        <Grid container spacing={2}>
          {images.map((img, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Box
                onClick={() => setPreviewImage(img)}
                sx={{
                  width: "100%",
                  height: 220,
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid #ddd",
                  cursor: "pointer",
                  backgroundColor: "#fff",
                }}
              >
                <img
                  src={img}
                  alt={`AI result ${index + 1}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const ProviderResultBox = ({
    provider,
    title,
    result,
    selectedProvider,
    onSelect,
  }) => {
    const isSelected = selectedProvider === provider;

    return (
      <Paper
        elevation={isSelected ? 4 : 1}
        sx={{
          p: 3,
          borderRadius: 3,
          backgroundColor: isSelected ? "#f0f7ff" : "#fafafa",
          direction: "rtl",
          textAlign: "right",
          border: isSelected ? "2px solid #1976d2" : "1px solid #e0e0e0",
          height: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {title}
          </Typography>

          {isSelected && (
            <Chip
              icon={<CheckCircleIcon />}
              label="تم الاختيار"
              color="primary"
              size="small"
            />
          )}
        </Box>

        <Typography sx={{ whiteSpace: "pre-wrap", lineHeight: 2 }}>
          {result?.textReply || "لا توجد نتيجة نصية."}
        </Typography>

        {result?.ok === false && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            هذه النتيجة لم تكتمل من المزود.
          </Alert>
        )}

        {renderGeneratedImages(result?.generatedImages || [])}

        <Button
          fullWidth
          variant={isSelected ? "contained" : "outlined"}
          sx={{ mt: 3 }}
          onClick={() => onSelect(provider)}
        >
          {isSelected ? "مختار" : "اختيار هذه النتيجة"}
        </Button>
      </Paper>
    );
  };

 const DualResultBox = ({ result, selectedProvider, onSelect }) => {
  if (!result) return null;

  const hasOpenAI = Boolean(result.openai);
  const hasGemini = Boolean(result.gemini);

  return (
    <Grid container spacing={2} sx={{ mt: 2 }}>
      {hasOpenAI && (
        <Grid item xs={12} md={hasGemini ? 6 : 12}>
          <ProviderResultBox
            provider="openai"
            title="نتيجة OpenAI"
            result={result.openai}
            selectedProvider={selectedProvider}
            onSelect={onSelect}
          />
        </Grid>
      )}

      {hasGemini && (
        <Grid item xs={12} md={6}>
          <ProviderResultBox
            provider="gemini"
            title="نتيجة Gemini"
            result={result.gemini}
            selectedProvider={selectedProvider}
            onSelect={onSelect}
          />
        </Grid>
      )}
    </Grid>
  );
};
  if (settingsLoading) {
    return (
      <Container sx={{ mt: 8, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>جاري تحميل إعدادات Artika AI...</Typography>
      </Container>
    );
  }

  if (!settings.enabled) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Alert severity="warning">
          خدمة Artika AI متوقفة حالياً من لوحة تحكم المدير.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, md: 4 },
          borderRadius: 3,
          minHeight: "80vh",
          direction: "rtl",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
            mb: 3,
          }}
        >
          <Typography variant="h4" fontWeight="bold">
            🤖 {settings.title || "Artika AI Assistant"}
          </Typography>

          <Button
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={resetWorkflow}
            disabled={loading}
          >
            محادثة جديدة
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {step === "wall" && (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              backgroundColor: "#f7f7f7",
            }}
          >
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
              الخطوة الأولى
            </Typography>

            <Typography variant="h6" sx={{ mb: 2 }}>
              {settings.welcomeMessage || "ارفع صورة جدار الزبون"}
            </Typography>

            <Typography color="text.secondary" sx={{ mb: 3 }}>
              ارفعي صورة واحدة فقط للجدار، واكتبي أي ملاحظة عندك.
            </Typography>

            <Button
              variant="contained"
              component="label"
              startIcon={<AttachFileIcon />}
              disabled={loading}
            >
              رفع صورة الجدار
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleWallFileChange}
              />
            </Button>

            {renderSelectedImagePreview(wallFiles, removeWallFile)}

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="ملاحظة الموظف"
              placeholder="اكتبي أي ملاحظة عن الجدار أو طلب الزبون..."
              value={wallNote}
              onChange={(e) => setWallNote(e.target.value)}
              sx={{ mt: 3 }}
            />

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                size="large"
                onClick={submitWallAnalysis}
                disabled={loading || wallFiles.length !== 1}
              >
                {loading ? "جاري التحليل..." : "تحليل صورة الجدار"}
              </Button>
            </Box>
          </Paper>
        )}

        {step === "wallResult" && wallResult && (
          <>
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
              نتيجة تحليل الجدار
            </Typography>

            <Typography color="text.secondary" sx={{ mb: 2 }}>
              اختاري النتيجة الأنسب حتى نكمل للمرحلة التالية.
            </Typography>

            <DualResultBox
              result={wallResult}
              selectedProvider={selectedWallProvider}
              onSelect={selectWallResult}
            />

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                onClick={continueToOptions}
                disabled={loading || !selectedWallProvider}
              >
                اختيار نوع الطلب التالي
              </Button>
            </Box>
          </>
        )}

        {step === "options" && (
          <Box>
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
              اختاري واحد فقط من الخيارات التالية
            </Typography>

            <Grid container spacing={2}>
              {["option1", "option2", "option3", "option4"].map(
                (optionType) => (
                  <Grid item xs={12} md={6} key={optionType}>
                    <Paper
                      elevation={2}
                      onClick={() => chooseOption(optionType)}
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        cursor: "pointer",
                        minHeight: 150,
                        border: "1px solid transparent",
                        "&:hover": {
                          borderColor: "#1976d2",
                          backgroundColor: "#f5f9ff",
                        },
                      }}
                    >
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                        {getOptionLabel(optionType)}
                      </Typography>

                      <Typography color="text.secondary">
                        {optionDescriptions[optionType]}
                      </Typography>
                    </Paper>
                  </Grid>
                )
              )}
            </Grid>
          </Box>
        )}

        {step === "optionForm" && (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              backgroundColor: "#f7f7f7",
            }}
          >
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
              {getOptionLabel(selectedOption)}
            </Typography>

            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {optionDescriptions[selectedOption]}
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              سيتم استخدام نتيجة{" "}
              {selectedWallProvider === "openai" ? "OpenAI" : "Gemini"} من
              تحليل الجدار كسياق لهذه المرحلة.
            </Alert>

            {selectedOption === "option4" && (
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="فكرة اللوحة"
                placeholder="اكتبي فكرة اللوحة التي يريدها الزبون..."
                value={ideaText}
                onChange={(e) => setIdeaText(e.target.value)}
                sx={{ mb: 3 }}
              />
            )}

            <Button
              variant="contained"
              component="label"
              startIcon={<AttachFileIcon />}
              disabled={loading}
            >
              رفع صور
              <input
                type="file"
                hidden
                multiple
                accept="image/*"
                onChange={handleOptionFilesChange}
              />
            </Button>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              الحد الأقصى للصور: {settings.maxOptionImages || 6}
            </Typography>

            {renderSelectedImagePreview(optionFiles, removeOptionFile)}

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="ملاحظة إضافية"
              placeholder="اكتبي أي ملاحظة إضافية..."
              value={optionNote}
              onChange={(e) => setOptionNote(e.target.value)}
              sx={{ mt: 3 }}
            />

            <Box sx={{ mt: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                size="large"
                onClick={submitOptionAnalysis}
                disabled={loading}
              >
                {loading ? "جاري إنشاء النتيجة..." : "إنشاء النتيجة النهائية"}
              </Button>

           <Button
  variant="outlined"
  onClick={() => setStep("wallResult")}
  disabled={loading}
>
  رجوع لتحليل الجدار
</Button>
            </Box>
          </Paper>
        )}

        {step === "finalResult" && finalResult && (
          <>
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
              النتيجة النهائية
            </Typography>

            <Typography color="text.secondary" sx={{ mb: 2 }}>
              اختاري النتيجة التي تعجبك من OpenAI أو Gemini.
            </Typography>

            <DualResultBox
              result={finalResult}
              selectedProvider={selectedFinalProvider}
              onSelect={selectFinalResult}
            />

            <Box sx={{ mt: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Button variant="contained" onClick={() => setStep("options")}>
                اختيار خيار آخر
              </Button>

              <Button variant="outlined" onClick={resetWorkflow}>
                بدء من جديد
              </Button>
            </Box>
          </>
        )}

        {loading && (
          <Box
            sx={{
              mt: 3,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CircularProgress size={22} />
            <Typography>جاري المعالجة، انتظري شوي...</Typography>
          </Box>
        )}

        <Dialog
          open={Boolean(previewImage)}
          onClose={() => setPreviewImage(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogContent
            sx={{
              p: 1,
              backgroundColor: "#111",
              position: "relative",
            }}
          >
            <IconButton
              onClick={() => setPreviewImage(null)}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: "#fff",
                backgroundColor: "rgba(0,0,0,0.5)",
                zIndex: 2,
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.75)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>

            {previewImage && (
              <img
                src={previewImage}
                alt="preview"
                style={{
                  width: "100%",
                  maxHeight: "80vh",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </Paper>
    </Container>
  );
};

export default ArtikaAI;